import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from "react-query"
import { PricingVersion, RunStatus } from "../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/versions"
import { useAppState } from "../redux"
import { useState } from 'react'
import { useHistory } from 'react-router'
import { QueryKeys } from '../../constants/query-keys'
import { notifications } from '@mantine/notifications'
import { PricingRule } from '../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/rules'


export const usePricingVersion = (id: string) => {
  const [enablePolling, setEnablePolling] = useState(true)
  const appState = useAppState()
  const { csrf_timestamp, csrf_token, masterAccountId } = appState.session
  const queryClient = useQueryClient()

  const getPricingVersion = async (): Promise<PricingVersion | undefined> => {
    const response = await axios.get(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${id}?csrf_token=${csrf_token}&csrf_timestamp=${csrf_timestamp}`)
    return response?.data?.data
  }

  const query = useQuery([QueryKeys.GLO_JAM_PRICING_VERSION, { id }], getPricingVersion, {
    refetchInterval: enablePolling ? 500 : 0, enabled: !!id,
    refetchIntervalInBackground: true,
    onSuccess: (data) => {
      if (data?.runStatus !== RunStatus.RUNNING) {
        setEnablePolling(false)
        queryClient.invalidateQueries([QueryKeys.GLO_JAM_REVIEW_MATCHES, { id }])
      } else {
        setEnablePolling(true)
      }
    }
  })

  return { ...query, polling: enablePolling }
}

export const usePricingVersionCache = (id: string) => {
  const key = [QueryKeys.GLO_JAM_PRICING_VERSION, { id }]
  const queryClient = useQueryClient()
  const cache = queryClient.getQueryData<PricingVersion>(key)
  return cache as PricingVersion
}

export const useCreatePricingVersion = () => {
  const { session: { masterAccountId, csrf_timestamp, csrf_token }} = useAppState()
  const history = useHistory()

  const createVersion = async ({ name, version, rules }: { name: string, version?: PricingVersion | null, rules?: PricingRule | null }) => {
    const createDefaultVersionbody = {
      csrf_token,
      csrf_timestamp,
      isCurrent: false,
      jobCount: 0,
      matchStrategy: 'current',
      name: name || 'New Version',
      propagateMatchOption: 'all'
    }
    const newVersion = version ? { ...createDefaultVersionbody, ...version, name, cloneVersionId: version.id } : createDefaultVersionbody
    const createVersionResponse = await axios.post(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions`, newVersion)
    const newVersionId = createVersionResponse?.data?.data?.id
    const createDefaultRulesBody = { csrf_token, csrf_timestamp, globalJobPricingVersionId: newVersionId, name: 'Jobs' }
    const newRules = rules ? { ...createDefaultRulesBody, ...rules, globalJobPricingVersionId: newVersionId, name: 'Jobs' } : createDefaultRulesBody
    await axios.post(`/cms/api/accounts/${masterAccountId}/global-job-pricing/rules`, newRules)
    return createVersionResponse.data.data
  }
  
  return useMutation(createVersion, {
    onSuccess: async (data) => {
      history.push(`strategy-based-pricing/${data.id}`)
    }
  })
}

export const useUpdatePricingVersion = (versionId: string) => {
  const appState = useAppState()
  const queryClient = useQueryClient()
  const { masterAccountId } = appState.session
  const version = usePricingVersionCache(versionId)

  const updatePricingVersion = async (fieldsToUpdate: Partial<PricingVersion>) => {
    const updatedVersion = { ...version, ...fieldsToUpdate }
    const response = await axios.put(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${versionId}`, updatedVersion)
    return response.data
  }

  const mutation = useMutation({
    mutationFn: updatePricingVersion,
    onMutate: async (fieldsToUpdate) => {
      await queryClient.cancelQueries()
      const updatedVersion = { ...version, ...fieldsToUpdate }
      queryClient.setQueryData([QueryKeys.GLO_JAM_PRICING_VERSION, { id: versionId }], updatedVersion)
      return version
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_VERSION, { id: versionId }])
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_VERSIONS])
    },
    onError: (error, _, context) => {
      queryClient.setQueryData([QueryKeys.GLO_JAM_PRICING_VERSION, { id: versionId }], context?.version);
      console.error('Mutation failed:', error);
    }
  })

  return mutation
}

export const useDeletePricingVersion = () => {
  const appState = useAppState()
  const queryClient = useQueryClient()
  const { masterAccountId } = appState.session

  const deletePricingVersion = async (versionId: string | number) => {
    const response = await axios.delete(`/cms/api/accounts/${masterAccountId}/global-job-pricing/versions/${versionId}`)
    return response.data
  }

  const mutation = useMutation({
    mutationFn: deletePricingVersion,
    onSuccess: () => {
      notifications.show({ title: 'Version Deleted', message: 'Successfully deleted pricing version.' })
    },
    onSettled: () => {
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_VERSIONS])
    },
    onError: () => {
      notifications.show({ title: 'Error', message: 'Failed deleting pricing version.', color: 'red' })
    }
  })

  return mutation
}
